import '@gl/lit/components/base/icon/index.js';

import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
  mergeResults,
  extendArchitecture,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {TemplateResult} from 'lit';
import {html, nothing} from 'lit';
import {when} from 'lit/directives/when.js';
import type {StyleInfo, StyleMapDirective} from 'lit/directives/style-map.js';
import {styleMap} from 'lit/directives/style-map.js';
import type {ClassInfo} from 'lit/directives/class-map.js';
import {classMap} from 'lit/directives/class-map.js';
import type {VariantsFor, SubModelFor} from '@gl/lit/types/architecture.js';
import type {Nullable} from '@gl/lit/types/general.js';
import {change} from '@gl/lit/utils/commands/index.js';
import {range} from '@gl/lit/utils/arrays.js';
import {option} from 'fp-ts';
import {pipe} from 'fp-ts/function';
import type {Point} from '@gl/lit/lib/point.js';
import type CanvasNode from './index.js';
import {DEFAULT_OUTPUT_LIMIT} from './constants.js';
import CanvasElement from '../canvas_element/index.js';
import type CanvasGroup from '../canvas_group/index.js';
import type CanvasConnection from '../canvas_connection/index.js';
import * as Super from '../canvas_element/architecture.js';
import type {DirectiveResult} from 'lit/directive.js';
import {applyStylesToElement, toCssValue} from '@gl/lit/utils/dom.js';
import {isCanvasGroupType} from '@gl/lit/types/canvas.js';

export type Model = {
  labels?: string[];
  canvasTrackerLabel?: string;
  group?: HTMLElement;
  connection: Nullable<HTMLElement>;
  inputs: Set<HTMLElement>;
  outputs: Record<number, HTMLElement>;
  outputLimit: number;
} & Super.Model;

type SubModel = SubModelFor<Model, Super.Model>;

export const Msg = makeVariants({
  setConnection: Variant<HTMLElement>()('set-connection'),
  setOutput: Variant<[number, HTMLElement]>()('set-output'),
  removeOutput: Variant<HTMLElement>()('remove-output'),
  removeOutputByIndex: Variant<number>()('remove-output-by-index'),
  removeAllOutputs: Variant<void>()('remove-all-outputs'),
  addInput: Variant<HTMLElement>()('add-input'),
  removeInput: Variant<HTMLElement>()('remove-input'),
  setGroup: Variant<HTMLElement>()('set-group'),
  setPosition: Variant<Point>()('set-position'),
  ...Super.Msg,
});

export type Msg = VariantsFor<typeof Msg>;

const SuperProgram = extendArchitecture<Super.Model, Super.Msg, Model, Msg>(
  Super.main
);

// -- Init

function init(
  props: InitialProperties,
  cmd: Cmd,
  model?: Model
): ImmutableResult<Model> {
  const outputLimit = <number>props.get('outputLimit') ?? DEFAULT_OUTPUT_LIMIT;
  const group = <CanvasGroup>props.get('group') ?? null;
  const connection = <CanvasConnection>props.get('connection') ?? null;
  const outputs = <Model['outputs']>props.get('outputs') ?? [];
  const inputs = (props.get('inputs') as HTMLElement[])?.length
    ? new Set<HTMLElement>(<HTMLElement[]>props.get('inputs'))
    : new Set<HTMLElement>();

  const result: SubModel = {
    ...model,
    group,
    connection,
    outputs,
    inputs,
    outputLimit,
  };

  return mergeResults(SuperProgram.init(props, cmd), result);
}

// -- Update

function whenAttached(host: CanvasNode) {
  host.$canvas?.cursor
    .on('drag', e => {
      if (host.isInPath(e.dragPath ?? [])) {
        host.select();
        return;
      }

      if (CanvasElement.isInPath(e.dragPath ?? [])) {
        host.deselect();
        return;
      }
    })
    .on('up', e => {
      if (e.ctx.hasDragged) return;

      pipe(
        e.composedPath ?? [],
        option.fromPredicate(host.isInPath.bind(host)),
        option.match(
          () => {
            CanvasElement.isInPath(e.composedPath ?? [])
              ? host.deselect()
              : host.deselect() && host.$canvas?.deselectAll();
          },
          () => host.select()
        )
      );
    });
}

export function reposition(host: CanvasNode, group: CanvasGroup) {
  const position = group.getNodePosition(host.key);
  if (!position) return;

  host.handleUpdate(Msg.setPosition(position));
}

export function attachGroupPositionListeners(group: CanvasGroup) {
  return function (host: CanvasNode) {
    group.addEventListener('moved', () => {
      reposition(host, group);
    });

    reposition(host, group);
  };
}

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-group': {
      if (!isCanvasGroupType(msg.value) || model.group) return model;

      return [
        {...model, group: msg.value},
        cmd(attachGroupPositionListeners(msg.value as CanvasGroup)),
      ] as const;
    }

    case 'set-position': {
      return [
        {...model, position: msg.value},
        cmd((host: CanvasElement) => {
          host.dispatchEvent(host.createEvent('moved'));
        }),
      ] as const;
    }

    case 'set-attached': {
      return mergeResults(
        [model, cmd(whenAttached)],
        SuperProgram.update(msg, model, cmd)
      );
    }
    case 'set-connection':
      return {...model, connection: msg.value} as const;

    case 'add-input': {
      model.inputs.add(msg.value);

      return [
        {
          ...model,
          inputs: new Set(model.inputs),
        },
        cmd(change),
      ] as const;
    }

    case 'remove-input': {
      model.inputs.delete(msg.value);

      return [
        {
          ...model,
          inputs: new Set(model.inputs),
        },
        cmd(change),
      ] as const;
    }

    case 'set-output': {
      const [index, output] = msg.value;
      const outputs = {
        ...model.outputs,
        [index]: output,
      };

      return [
        {
          ...model,
          outputs,
        },
        cmd(change),
      ] as const;
    }

    case 'remove-output': {
      let idx = -1;

      for (const [key, _output] of Object.entries(model.outputs)) {
        const output = _output as CanvasConnection;
        if (output.key !== (msg.value as CanvasConnection).key) continue;
        idx = Number(key);
        break;
      }

      if (idx === -1) return model;

      delete model.outputs[idx];

      return [{...model}, cmd(change)] as const;
    }

    case 'remove-output-by-index': {
      if (!model.outputs[msg.value]) return model;
      delete model.outputs[msg.value];

      return [{...model}, cmd(change)] as const;
    }

    case 'remove-all-outputs': {
      const outputs: Model['outputs'] = {};

      return [
        {
          ...model,
          outputs,
        },
        cmd(change),
      ] as const;
    }
  }

  return SuperProgram.update(msg, model, cmd);
}

// -- View

function nodeStyles(model: Model): StyleInfo {
  if (model.group) return {};

  return {
    left: toCssValue(model.position.x),
    top: toCssValue(model.position.y),
  };
}

function nodeClasses(model: Model): ClassInfo {
  return {
    'canvas-node--selected': model.selected,
    'canvas-node--focused': model.focused,
    'canvas-node--connected': !!model.inputs.size,
  };
}

function connectorClasses(host: CanvasNode, index: number): ClassInfo {
  return {
    'canvas-node__connector--connected': host.hasValidConnection({index}),
  };
}

function handleConnectorClick(e: Event, host: CanvasNode, index: number) {
  e.stopImmediatePropagation();
  e.preventDefault();

  host.hasValidConnection({index})
    ? host.destroyConnection({index})
    : host.createConnection({index});
}

function renderConnectorContent(
  host: CanvasNode,
  index: number,
  styles: DirectiveResult<typeof StyleMapDirective>
) {
  return html`
    <div
      class="canvas-node__connector ${classMap(connectorClasses(host, index))}"
      style="${styles}"
      @mousedown=${(e: Event) => handleConnectorClick(e, host, index)}
    >
      ${when(
        host.hasValidConnection({index}),
        () => html`<fly-icon icon="close"></fly-icon>`,
        () => nothing
      )}
    </div>
  `;
}

function renderConnectorsWithLabels(
  host: CanvasNode,
  index: number,
  styles: DirectiveResult<typeof StyleMapDirective>,
  model: Model
) {
  if (!model.labels) return;
  return html`
    <div
      id="connector-wrapper-${index}"
      class="canvas-node__connector-wrapper canvas-node__connector-wrapper--criteria"
    >
      <fly-tooltip content=${model.labels[index]}>
        ${renderConnectorContent(host, index, styles)}
      </fly-tooltip>
    </div>
  `;
}

export function viewConnector(host: CanvasNode, index: number, model: Model) {
  const styles = styleMap({
    'background-color': `var(--canvas-node-connector-${
      index + 1
    }-bg, var(--canvas-node-connector-bg))`,
    'border-color': `var(--canvas-node-connector-${
      index + 1
    }-border-color, var(--canvas-node-connector-border-color))`,
  });

  if (model.labels?.length) {
    return renderConnectorsWithLabels(host, index, styles, model);
  }

  return html`
    <div id="connector-wrapper-${index}" class="canvas-node__connector-wrapper">
      ${renderConnectorContent(host, index, styles)}
    </div>
  `;
}

function renderCanvasTracker(host: CanvasNode) {
  if (host.model.canvasTrackerLabel) {
    return html`
      <div class="canvas-node__connectors-top">
        <div class="canvas-node--wrapper">
          <fly-tooltip content=${host.model.canvasTrackerLabel || ''}>
            <div class="canvas-node__tracker"></div>
          </fly-tooltip>
        </div>
      </div>
    `;
  }

  return html` <div class="canvas-node__connectors-top">
    <div class="canvas-node--wrapper">
      <div class="canvas-node__tracker"></div>
    </div>
    </div>
  </div>`;
}

export function viewNode(
  host: CanvasNode,
  model: Model,
  template: TemplateResult,
  additionalClasses?: ClassInfo
) {
  const combinedClasses = {
    ...nodeClasses(model),
    ...additionalClasses,
  };

  applyStylesToElement(host, nodeStyles(model));

  return html`
    <div
      class="canvas-node ${classMap(combinedClasses)}"
      @mousedown="${() => host.updateZIndex()}"
    >
      ${renderCanvasTracker(host)} ${template}
      <div class="canvas-node__connectors">
        ${[...range(0, model.outputLimit - 1)].map((_, index) => {
          return viewConnector(host, index, model);
        })}
      </div>
    </div>
  `;
}

function view(this: CanvasNode, model: Readonly<Model>) {
  return viewNode(this, model, html`<slot></slot>`);
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
